import { makeObservable, action, computed, observable } from 'mobx'
import type ReCaptcha from 'react-google-recaptcha'
import * as Sentry from '@sentry/react'
import type { StripeElementsOptionsMode } from '@stripe/stripe-js'
import { PAYMENT_FORMS, PAYMENT_PROVIDERS } from 'constants/paymentSettingShared.constants'
import { isNotOneTimePlanForm } from 'constants/pricingPlans.constants'
import { DEFAULT_CURRENCY } from 'constants/currencies.constants'
import { VAT_TYPE } from 'constants/ordersShared.constants'
import { CURRENT_TIMEZONE } from '@elo-kit/constants/dateTime.constants'
import { TRANSFER_PROVIDERS_BASE } from 'constants/transfers.constants'
import { CAPTURE_METHOD } from 'constants/stripe.constants'
import { P24_PAYMENT_KEY } from 'constants/paymentSetting.constants'
import { getMP3dsBrowserMetaData } from 'utils/requests.utils'
import {
  handleTransactionResponse,
  convertPriceForStripe,
  handleStripePaymentElementCard,
  handleStripePaymentElementTransaction,
  getStripeLocale,
} from '@elo-kit/utils/stripe.utils'
import { get } from 'utils/lodash.utils'
import { toAlpha2CountryCode } from 'utils/countries.utils'
import { getSellerLink } from 'utils/helpersShared.utils'
import { createFingerPrint } from 'utils/fingerprint.utils'
import { ClickSubmitData } from 'shop/types/stripe'
import SharedStore from 'shared/stores/shared.store'
import { ShopRootStore } from 'shop/stores/shopRoot.store'

import { CreateOrderResponse } from 'shop/api/orders.api'

import { capitalizeFirstLetter } from 'utils/nameStyle.utils'
import { isSetupIntentPossible } from 'shared/components/purchase-methods/expressCheckout.utils'
import { normalizeDealPaymentMethodChoice } from 'shop/utils/deal-payment-method-choice.utils'
import { DEAL_METHOD_NOT_PERMITTED, type DealPaymentMethodChoice } from 'shop/types/deal-payment-method-choice'
import { DealsApi, createDealsApi, Deal } from '../api/deals.api'
import {
  CreateCardRegistrationResult,
  CardForm,
  MangoPayCardPayment,
} from '../../interfaces/payment-method-interfaces/MangoPayCardPayment'
import {
  PAYMENT_ELEMENTS_MODE,
  StripeElementsPayloads,
} from '../../interfaces/payment-method-interfaces/StripePaymentMethod'

export class DealsStore extends SharedStore<Deal> implements MangoPayCardPayment, StripeElementsPayloads {
  storeName = 'DealsStore'
  root: ShopRootStore
  declare childApi: DealsApi
  @observable paramsForSubmit = {
    transfer: {},
  }
  @observable coupon = {}
  @observable terms = {}
  @observable isValidForSubmit = true
  @observable termsAndCondition = false
  @observable recaptchaRef: ReCaptcha = null

  @observable mangoPayForm = null
  @observable mangoPayOrderResponse = null

  @observable buyBtnDisabled = false

  /**
   * SCL-1894 R2-AC4 — our own wording for a refused attempt, shown in the page.
   * The same observable/setter pair `ordersStore` and `paymentStore` already
   * carry; `PurchaseMethods` reads it through `getCurrentPaymentStore()`.
   * Never holds provider text.
   */
  @observable purchaseError = ''
  @observable purchaseErrorMethod = ''

  /**
   * SCL-1894 R2-AC3 — methods refused in *this* session. The contract's
   * `refusedMethods` is the durable half (it survives the provider redirect);
   * this is the immediate half, for a decline the webhook has not landed yet.
   * The exhausted state is the union of the two (spec §3.6c).
   */
  @observable refusedMethodsInSession: string[] = []
  /** The deal the session state above belongs to. The shop root store is a
   *  client-side singleton, so a client-side move to another deal would
   *  otherwise inherit its refusals and look exhausted on arrival. */
  @observable sessionDealToken = ''

  @computed get sessionRefusedMethods(): string[] {
    return this.sessionDealToken === this.item?.token ? this.refusedMethodsInSession : []
  }

  @action setRecaptchaRef = (ref: ReCaptcha) => (this.recaptchaRef = ref)
  @action setMangoPayOrderResponse = (object) => (this.mangoPayOrderResponse = object)
  @action setBuyBtnDisabling = (value) => (this.buyBtnDisabled = value)
  @action setPurchaseError = (error: string) => (this.purchaseError = error)

  /**
   * SCL-1894 — the single exit path for a refused attempt.
   *
   * Does the four things a refusal has to do, in one place so no caller can do
   * three of them: shows *our* wording in the page (R2-AC4 — never the
   * provider's), records the method so the exhausted state can be derived
   * (R2-AC3), and clears both the loading mask and the buy button so the payer
   * can actually pick something else (§3.7 — a refusal that left `loading` true
   * would freeze the list and reproduce the very bug this epic fixes).
   *
   * `providerMessage` is accepted only so callers need not decide what to do
   * with it; it is deliberately never shown to the payer.
   */
  @action handleRefusedAttempt = (method?: string, providerMessage?: string) => {
    const refusedMethod = method || this.activePayMethod

    /* Unconditional: whatever the gate says, a refusal that left `loading` true
     * would freeze the page. */
    this.toggleLoading(false)
    this.setBuyBtnDisabling(false)

    /* Everything below is this epic's, so an ungated seller's payer keeps the
     * provider toast they get today and none of the new state (R5-AC1). */
    if (!this.paymentMethodChoice.enabled) {
      return
    }

    this.sessionDealToken = this.item?.token
    this.setPurchaseError(I18n.t('react.shop.payment.deal.attempt_failed'))
    this.purchaseErrorMethod = refusedMethod
    this.addSessionRefusedMethod(refusedMethod)

    if (providerMessage) {
      Sentry.addBreadcrumb({ category: 'deal-payment', message: 'provider refused the attempt', level: 'info' })
    }
  }

  @action addSessionRefusedMethod = (method: string) => {
    if (!method || this.sessionRefusedMethods.includes(method)) {
      return
    }

    this.sessionDealToken = this.item?.token
    this.refusedMethodsInSession = [...this.sessionRefusedMethods, method]
  }

  /**
   * The payer's offered set for this deal (SCL-1894 contract v1, spec §4).
   *
   * `keepAlive` is deliberate: `PurchaseMethods` resets the payer's selection
   * whenever `allowedMethods` changes identity and is not deep-equal, so a
   * computed that rebuilt its array on every read would fight the pre-selection
   * (spec §3.1, §3.11).
   *
   * The order payload wins over the deal payload because checkout is
   * authoritative (R7-AC3); on the acceptance page there is no order yet.
   */
  @computed({ keepAlive: true }) get paymentMethodChoice(): DealPaymentMethodChoice {
    const fromOrder = this.root.ordersStore?.item?.paymentMethodChoice
    const fromDeal = this.item?.paymentMethodChoice

    return normalizeDealPaymentMethodChoice(fromOrder || fromDeal)
  }

  /** The refusal message, while it still describes what the payer has selected. */
  @computed get currentPurchaseError(): string {
    if (this.sessionDealToken !== this.item?.token) {
      return ''
    }

    return this.purchaseErrorMethod === this.activePayMethod ? this.purchaseError : ''
  }

  /** The payer's current selection, falling back to the deal's stored method before first paint. */
  @computed get activePayMethod(): string {
    return this.root.purchaseMethodsStore?.paymethods?.form || this.item?.selectedPayMethod
  }

  showRecaptcha = () => {
    const recaptchaConfig = this.root.experimentsStore.getDynamicConfig('recaptcha_config')

    return recaptchaConfig.get(this.activePayMethod, false)
  }

  @computed get isPayAllowed() {
    const selectedPayMethod = this.activePayMethod
    const { cardProvider } = this.root.sellerStore.item

    if (
      selectedPayMethod === PAYMENT_FORMS.card &&
      (cardProvider === PAYMENT_PROVIDERS.stripe || cardProvider === PAYMENT_PROVIDERS.elopageConnect)
    ) {
      return this.root.purchaseMethodsStore.stripeCardValid
    }

    return true
  }

  @computed get paymethodsData(): { transfer: { creditCard: string | { num: string }; form: string; iban: string } } {
    const { creditCard: paymethodCard, iban } = this.root.purchaseMethodsStore.paymethods || {}
    const creditCard = paymethodCard

    return {
      transfer: {
        creditCard,
        form: this.activePayMethod,
        iban,
      },
    }
  }

  getSepaStripeElementPayload = (): StripeElementsOptionsMode => {
    const { sepaProvider, elopageConnectAccountId } = this.root.sellerStore.item

    return {
      mode: 'setup',
      paymentMethodCreation: 'manual',
      paymentMethodTypes: ['sepa_debit'],
      currency: this.currency || DEFAULT_CURRENCY,
      locale: getStripeLocale(),
      setupFutureUsage: 'off_session',
      ...(sepaProvider === PAYMENT_PROVIDERS.elopageConnect ? { onBehalfOf: elopageConnectAccountId } : {}),
    }
  }

  getKlarnaStripeElementPayload = (): StripeElementsOptionsMode => {
    const { klarnaProvider, elopageConnectAccountId } = this.root.sellerStore.item

    return {
      mode: 'payment',
      paymentMethodTypes: [PAYMENT_FORMS.klarna],
      currency: this.currency || DEFAULT_CURRENCY,
      amount: convertPriceForStripe(this.totalPrice()),
      locale: getStripeLocale(),
      ...(klarnaProvider === PAYMENT_PROVIDERS.elopageConnect ? { onBehalfOf: elopageConnectAccountId } : {}),
    }
  }

  getStripeElementOptions = (): StripeElementsOptionsMode => ({
    mode: 'payment',
    currency: this.currency,
    amount: convertPriceForStripe(this.totalPrice()),
    locale: getStripeLocale(),
  })

  getTwintStripeElementPayload = (mode?: string): StripeElementsOptionsMode => {
    const { twintProvider, elopageConnectAccountId } = this.root.sellerStore.item

    const data =
      mode === PAYMENT_ELEMENTS_MODE.setup
        ? {
            mode: PAYMENT_ELEMENTS_MODE.setup,
            currency: this.currency,
            locale: getStripeLocale(),
          }
        : this.getStripeElementOptions()

    const savesMandate = mode === PAYMENT_ELEMENTS_MODE.setup || isNotOneTimePlanForm(this.item?.pricingPlan?.form)

    return {
      ...data,
      ...(savesMandate ? { setupFutureUsage: 'off_session' } : {}),
      paymentMethodTypes: [PAYMENT_FORMS.twint],
      ...(twintProvider === PAYMENT_PROVIDERS.elopageConnect ? { onBehalfOf: elopageConnectAccountId } : {}),
    }
  }

  getMonduStripeElementPayload = (): StripeElementsOptionsMode => ({
    ...this.getStripeElementOptions(),
    paymentMethodTypes: [PAYMENT_FORMS.mondu],
  })

  getPayByBankStripeElementPayload = (): StripeElementsOptionsMode => {
    const { payByBankProvider, elopageConnectAccountId } = this.root.sellerStore.item
    return {
      ...this.getStripeElementOptions(),
      paymentMethodTypes: [PAYMENT_FORMS.payByBank],
      ...(payByBankProvider === PAYMENT_PROVIDERS.elopageConnect ? { onBehalfOf: elopageConnectAccountId } : {}),
    }
  }

  getStripeCardElementPayload = (mode?: string): StripeElementsOptionsMode => {
    const { cardProvider, elopageConnectAccountId } = this.root.sellerStore.item

    const data =
      mode === PAYMENT_ELEMENTS_MODE.setup
        ? {
            mode: PAYMENT_ELEMENTS_MODE.setup,
            currency: this.currency,
            locale: getStripeLocale(),
          }
        : this.getStripeElementOptions()

    return {
      ...data,
      setupFutureUsage: 'off_session',
      paymentMethodTypes: [PAYMENT_FORMS.card],
      ...(cardProvider === PAYMENT_PROVIDERS.elopageConnect ? { onBehalfOf: elopageConnectAccountId } : {}),
    }
  }

  getIdealStripeElementPayload = (mode): StripeElementsOptionsMode => {
    const { idealProvider, elopageConnectAccountId } = this.root.sellerStore.item

    const data =
      mode === PAYMENT_ELEMENTS_MODE.setup
        ? {
            mode: PAYMENT_ELEMENTS_MODE.setup,
            currency: this.currency,
            locale: getStripeLocale(),
          }
        : this.getStripeElementOptions()

    return {
      ...data,
      ...(mode === PAYMENT_ELEMENTS_MODE.setup ? { setupFutureUsage: 'off_session' } : {}),
      paymentMethodTypes: [PAYMENT_FORMS.ideal],
      ...(idealProvider === PAYMENT_PROVIDERS.elopageConnect ? { onBehalfOf: elopageConnectAccountId } : {}),
    }
  }

  getP24StripeElementPayload = (): StripeElementsOptionsMode => {
    const { p24Provider, elopageConnectAccountId } = this.root.sellerStore.item
    return {
      ...this.getStripeElementOptions(),
      paymentMethodTypes: [P24_PAYMENT_KEY],
      ...(p24Provider === PAYMENT_PROVIDERS.elopageConnect ? { onBehalfOf: elopageConnectAccountId } : {}),
    }
  }

  getStripeElementPayload = (paymentForm: string, mode: string): StripeElementsOptionsMode => {
    switch (paymentForm) {
      case PAYMENT_FORMS.card:
        return this.getStripeCardElementPayload(mode)
      case PAYMENT_FORMS.p24:
        return this.getP24StripeElementPayload()
      case PAYMENT_FORMS.ideal:
        return this.getIdealStripeElementPayload(mode)
      case PAYMENT_FORMS.payByBank:
        return this.getPayByBankStripeElementPayload()
      case PAYMENT_FORMS.klarna:
        return this.getKlarnaStripeElementPayload()
      case PAYMENT_FORMS.twint:
        return this.getTwintStripeElementPayload(mode)
      case PAYMENT_FORMS.mondu:
        return this.getMonduStripeElementPayload()
      default:
        return {} as StripeElementsOptionsMode
    }
  }

  @action setTerms = (terms?: any) => (this.terms = terms)

  @computed get prepareSellablesForSubmit() {
    const { product, pricingPlan } = this.root.ordersStore.item

    if (this.root.ordersStore.item.invoices?.[0]?.token) {
      return []
    } else {
      return [
        {
          productId: product?.id,
          sellableId: product?.id,
          sellableType: 'Product',
          pricingPlanId: pricingPlan?.id,
          priceCount: product?.count || 1,
          sellableItemsAttributes: [
            {
              productId: product?.id,
              ticketsCount: product?.count,
              ticketAttendeesAttributes: [],
            },
          ],
        },
      ]
    }
  }

  @computed get buildParams() {
    return {
      affiliateToken: null,
      productId: this.item.productId,
      productSlug: this.item.productSlug,
      pageProductId: this.item.productId,
      invoice: null,
      coupon: null,
      sellables: this.prepareSellablesForSubmit,
      vatData:
        this.item.payerData.formType && this.item.payerData.userProfileAttributes.vatNo
          ? {
              valid: true,
              dirty: true,
              validCountry: true,
            }
          : {},
      funnelId: null,
      transfer: {
        ...this.paymethodsData,
      },
      payer: {
        ...this.item.payerData,
      },
      owner: !!Object.keys(this.item.ownerData).length
        ? {
            ...this.item.ownerData,
          }
        : null,
    }
  }

  @computed get isDigitalPayment() {
    const form = this.activePayMethod
    return form === PAYMENT_FORMS.applePay || form === PAYMENT_FORMS.googlePay
  }

  @computed get planPrice() {
    return Number(this.item.pricingPlan?.prefs?.price) || 0
  }

  totalPrice = (orderRateIndex = 0) => {
    const { rates = [] } = this.root.ordersStore.item || {}

    if (!rates.length) {
      return orderRateIndex === 0 ? this.planPrice : 0
    }

    const { orderRatePrices = [] } = rates[orderRateIndex] || {}

    const total = orderRatePrices.reduce(
      (result, { data }) => {
        const {
          cfgs: { mustPayVat },
          discount,
          fees,
          rate,
        } = data || {}
        const priceKey = mustPayVat ? VAT_TYPE.gross : VAT_TYPE.net

        const getSum = (currentValue, value) =>
          value ? Number((currentValue + value[priceKey] * value.count).toFixed(2)) : currentValue

        const rateSum = getSum(result.rateSum, rate)
        const feesSum = getSum(result.feesSum, fees)
        const discountSum = getSum(result.discountSum, discount)

        return {
          rateSum,
          feesSum,
          discountSum,
        }
      },
      {
        rateSum: 0,
        feesSum: 0,
        discountSum: 0,
      }
    )

    return total.rateSum + total.feesSum + total.discountSum
  }

  @computed get currency() {
    return this.root.currenciesStore.getKey(this.item.pricingPlan?.currencyId) || DEFAULT_CURRENCY
  }

  handleCancellationTerm = async () => {
    const { formType } = this.item.payerData || {}
    const { cancellationTermId, b2bCancellationTermId } = this.root.productsStore.product
    const {
      item: { username },
    } = this.root.sellerStore
    const { item, fetchItem } = this.root.cancellationTermsStore
    const { includeType } = this.root.themeStore.ppTemplate.buyerInformation

    const hasB2BCancellationTerm = b2bCancellationTermId && b2bCancellationTermId !== item.id
    if (hasB2BCancellationTerm && (formType === 'business' || includeType.private === 'off')) {
      await fetchItem(b2bCancellationTermId, { username })
    } else if (cancellationTermId && cancellationTermId !== item.id) {
      await fetchItem(cancellationTermId, { username })
    }
  }

  handleRedirect = (redirectLink) => {
    if (!redirectLink) {
      window.location.href = getSellerLink(
        this.root.sellerStore.item,
        `/payment/${this.root.ordersStore.item.token}?funnel_on=true`
      )
      return
    }
    window.location.href = redirectLink
  }

  handleSubmitWithRecaptcha = () =>
    this.showRecaptcha()
      ? this.recaptchaRef.executeAsync().then((recaptchaValue: string) => {
          if (recaptchaValue) {
            this.recaptchaRef.reset()
            return this.payDeal({ recaptchaValue })
          }
          this.recaptchaRef.reset()
          this.setBuyBtnDisabling(false)
          return null
        })
      : this.payDeal()

  hideLoading = () => {}

  isSetupIntentPossibleFor = (paymentForm: string): boolean =>
    isSetupIntentPossible(this.root.ordersStore.item?.setupIntentPossible, paymentForm)

  handleDigitalExpressClick = (submitData?: ClickSubmitData) => {
    const { pricingPlan } = this.item
    const selectedPayMethod = this.activePayMethod
    const { prefs } = pricingPlan || {}

    const isDigitalPayment =
      selectedPayMethod === PAYMENT_FORMS.applePay || selectedPayMethod === PAYMENT_FORMS.googlePay
    const withSetupIntent = this.isSetupIntentPossibleFor(selectedPayMethod)
    const showDigitalPaymentModal =
      isDigitalPayment && submitData?.expressElement && (!prefs?.customStartDay || withSetupIntent)

    if (showDigitalPaymentModal) {
      /* With a SetupIntent nothing is charged today, so the manual-capture card fallback is not needed */
      if (prefs?.testPeriod && !withSetupIntent) {
        submitData.elements.update({
          captureMethod: CAPTURE_METHOD.manual,
          paymentMethodTypes: [PAYMENT_FORMS.card],
          onBehalfOf: null,
        })
      }

      submitData.showPopup()
    } else {
      this.payDeal(submitData)
    }
  }

  getBillingDetails = () => ({
    name: `${get(this.item, 'payerData.userProfileAttributes.firstName', '')} ${get(
      this.item,
      'payerData.userProfileAttributes.lastName',
      ''
    )}`,
    email: this.item.payerData.userAttributes.email,
    phone: this.item.payerData?.userProfileAttributes.phone || '',
    address: {
      city: this.item.payerData?.userProfileAttributes.city,
      country: toAlpha2CountryCode(this.item.payerData?.userProfileAttributes.countryCode),
      postal_code: this.item.payerData?.userProfileAttributes.zip,
      state: this.item.payerData?.userProfileAttributes.state || '',
      line1: this.item.payerData?.userProfileAttributes.street,
      line2: this.item.payerData?.userProfileAttributes.streetNumber || '',
    },
  })

  @action payDeal = async (submitData?: ClickSubmitData): Promise<CreateOrderResponse> => {
    if (this.buyBtnDisabled) return
    this.setBuyBtnDisabling(true)

    const { token } = this.item
    const selectedPayMethod = this.activePayMethod
    const { cardProvider } = this.root.sellerStore.item

    const isDigitalPayment =
      selectedPayMethod === PAYMENT_FORMS.applePay || selectedPayMethod === PAYMENT_FORMS.googlePay
    const isCard = selectedPayMethod === PAYMENT_FORMS.card
    const isMangoPay = cardProvider === PAYMENT_PROVIDERS.mangoPay
    const hasDifferentBillingAddress = !!this.item?.ownerData?.userProfileAttributes
    const { ownerData, payerData } = this.item
    const formType = hasDifferentBillingAddress ? 'business' : 'private' // TODO: remove this line, payerData includes correct formType

    const { stripeElements, stripeClient, setStripePaymentElementError, stripeAuthentificationLoadingToggle } =
      this.root.purchaseMethodsStore
    const recaptcha = submitData?.recaptchaValue ? { recaptcha: submitData.recaptchaValue } : {}

    const paramsForSubmit = {
      id: this.root.ordersStore.item.token,
      orderId: this.root.ordersStore.item.id,
      cancelTerms: this.terms,
      invoice: this.root.ordersStore.item.invoices[0]?.token || null,
      sellables: this.prepareSellablesForSubmit,
      deal: token,
      payer: {
        formType: payerData.formType || formType,
        formTypeForVat: payerData.formType || formType,
        userProfileAttributes: {
          countryCode: this.item.payerData.userProfileAttributes.countryCode,
          phone: this.item.payerData.userProfileAttributes.phone,
          company: this.item.payerData.userProfileAttributes.company,
          city: this.item.payerData.userProfileAttributes.city,
          street: this.item.payerData.userProfileAttributes.street,
          streetNumber: this.item.payerData.userProfileAttributes.streetNumber,
          zip: this.item.payerData.userProfileAttributes.zip,
          vatNo: this.item.payerData.userProfileAttributes.vatNo,
          firstName: (this.item.payerData.userProfileAttributes.firstName || '').trim(),
          lastName: (this.item.payerData.userProfileAttributes.lastName || '').trim(),
          additionalAddress: this.item.payerData.userProfileAttributes.additionalAddress,
        },
        userAttributes: {
          timeZoneName: CURRENT_TIMEZONE,
          email: this.item.payerData.userAttributes.email,
          emailConfirmation: this.item.payerData.userAttributes.emailConfirmation,
        },
      },
      owner: hasDifferentBillingAddress
        ? {
            userProfileAttributes: {
              countryCode: ownerData?.userProfileAttributes?.countryCode,
              phone: ownerData?.userProfileAttributes?.phone,
              company: ownerData?.userProfileAttributes?.company,
              city: ownerData?.userProfileAttributes?.city,
              street: ownerData?.userProfileAttributes?.street,
              streetNumber: ownerData?.userProfileAttributes?.streetNumber,
              zip: ownerData?.userProfileAttributes?.zip,
              vatNo: ownerData?.userProfileAttributes?.vatNo,
              firstName: ownerData?.userProfileAttributes?.firstName,
              lastName: ownerData?.userProfileAttributes?.lastName,
              additionalAddress: ownerData?.userProfileAttributes?.additionalAddress,
            },
            userAttributes: {
              timeZoneName: CURRENT_TIMEZONE,
              email: ownerData?.userAttributes?.email,
            },
          }
        : null,
      ...this.paramsForSubmit,
      ...this.paymethodsData,
      ...recaptcha,
    }

    if (this.root.purchaseMethodsStore.fraudSessionIdentifier) {
      const paypalFraudData = {
        paypalFraudToken: this.root.purchaseMethodsStore.fraudSessionIdentifier,
      }

      paramsForSubmit.transfer = {
        ...paramsForSubmit.transfer,
        ...paypalFraudData,
      }
    }

    if (isCard && isMangoPay) {
      const mp3dsBrowserMetaData = getMP3dsBrowserMetaData()

      paramsForSubmit.transfer = {
        ...paramsForSubmit.transfer,
        ...mp3dsBrowserMetaData,
      }
    }

    const methodsToSubmit = [
      PAYMENT_FORMS.klarna,
      PAYMENT_FORMS.p24,
      PAYMENT_FORMS.ideal,
      PAYMENT_FORMS.card,
      PAYMENT_FORMS.mondu,
      PAYMENT_FORMS.twint,
      PAYMENT_FORMS.payByBank,
      PAYMENT_FORMS.sepa,
    ]

    if (methodsToSubmit.includes(selectedPayMethod) && stripeElements) {
      const { error } = await stripeElements.submit()
      if (error) {
        setStripePaymentElementError(error)
        this.setBuyBtnDisabling(false)
        this.hideLoading()
        return
      }
    }

    if ((selectedPayMethod === PAYMENT_FORMS.sepa || selectedPayMethod === PAYMENT_FORMS.payByBank) && stripeElements) {
      const payerParams = {
        payment_method_data: {
          billing_details: this.getBillingDetails(),
        },
      }

      const { error, confirmationToken } = await stripeClient.createConfirmationToken({
        elements: stripeElements,
        params: payerParams,
      })

      if (error) {
        setStripePaymentElementError(error)
        this.setBuyBtnDisabling(false)
        return
      }

      if (!confirmationToken || !confirmationToken.id) {
        Sentry.captureException({
          name: `Deals: ${selectedPayMethod} confirmationToken error`,
          stipeElements: stripeElements,
          payerParams,
        })
      }

      const token = { confirmationToken: confirmationToken.id }

      paramsForSubmit.transfer = {
        ...paramsForSubmit.transfer,
        ...token,
      }
    }

    this.toggleLoading(true)
    const orderSessionId = createFingerPrint()

    const isMangoPayCardFlow = isCard && isMangoPay

    /* skipErrorNotific: a gated refusal is shown in the page in our own words,
     * so the raw server string must not also be toasted (R2-AC4). Ungated, the
     * toast is still the only message the payer gets, so it has to fire. */
    const resp = await this.root.ordersStore.childApi.createOrder(
      this.root.sellerStore.item.username,
      {
        ...paramsForSubmit,
        orderSessionId,
      },
      { skipErrorNotific: this.paymentMethodChoice.enabled }
    )

    const { clientSecret, redirectLink, success, setupIntentSecret } = resp

    if (resp && success) {
      if (isMangoPayCardFlow) {
        this.setMangoPayOrderResponse(resp)
        return resp
      }

      const isP24 = selectedPayMethod === PAYMENT_FORMS.p24
      const isIdeal = selectedPayMethod === PAYMENT_FORMS.ideal
      const isPayByBank = selectedPayMethod === PAYMENT_FORMS.payByBank
      const isTwintSetup =
        selectedPayMethod === PAYMENT_FORMS.twint && this.isSetupIntentPossibleFor(PAYMENT_FORMS.twint)

      if (
        selectedPayMethod === PAYMENT_FORMS.paypal &&
        (this.root.sellerStore.item.paypalProvider === TRANSFER_PROVIDERS_BASE.paypalRestPartner ||
          (this.root.sellerStore.item.paypalProvider === TRANSFER_PROVIDERS_BASE.paypalRest &&
            this.root.sellerStore.item.paypalRestV2))
      ) {
        return resp
      }

      if (
        (isP24 || isCard || isDigitalPayment || isIdeal || isPayByBank || isTwintSetup) &&
        (stripeElements || isDigitalPayment) &&
        (stripeClient || submitData?.stripeClient) &&
        (clientSecret || ((isCard || isDigitalPayment || isTwintSetup) && setupIntentSecret))
      ) {
        if (isP24 || isIdeal || isPayByBank) {
          handleStripePaymentElementTransaction(
            stripeElements,
            stripeClient,
            {
              billingDetails: this.getBillingDetails(),
              redirectLink,
            },
            clientSecret
          ).then((result) => {
            const { paymentIntent } = result

            if (paymentIntent) {
              this.handleRedirect(redirectLink)
              return
            }

            /* An immediate refusal on these three never reached a handler, so
               the page kept its loading mask indefinitely (R2-AC1). */
            this.handleRefusedAttempt(selectedPayMethod, result?.error?.message)
          })
        }

        if (isCard || isTwintSetup) {
          this.toggleLoading(false)
          stripeAuthentificationLoadingToggle(true)
          this.setBuyBtnDisabling(false)
          handleStripePaymentElementCard(
            stripeElements,
            stripeClient,
            {
              billingDetails: this.getBillingDetails(),
              redirectLink: isTwintSetup ? redirectLink : '',
            },
            clientSecret,
            setupIntentSecret
          ).then((result) => {
            handleTransactionResponse(
              result,
              () => this.handleRedirect(redirectLink),
              () => {
                stripeAuthentificationLoadingToggle(false)
                this.handleRefusedAttempt(selectedPayMethod, result?.error?.message)
              },
              /* our wording goes in the page instead — R2-AC4 */
              { suppressNotify: true }
            )
          })
        }

        if (isDigitalPayment) {
          return resp
        }
      } else {
        this.handleRedirect(redirectLink)
      }
    } else {
      /* Only a provider refusal means "this method was tried and failed". The
       * contract's other code, payment_method_not_permitted, is the guardrail
       * answering a stale offered set (spec §4): counting it as tried would
       * drive the exhausted state on a method the payer never got to use. */
      if (resp?.errorCode === DEAL_METHOD_NOT_PERMITTED) {
        this.setPurchaseError(I18n.t('react.shop.payment.deal.attempt_failed'))
        this.toggleLoading(false)
        this.setBuyBtnDisabling(false)
      } else {
        this.handleRefusedAttempt(selectedPayMethod, typeof resp?.error === 'string' ? resp.error : undefined)
      }

      if (isMangoPayCardFlow) {
        this.setMangoPayOrderResponse(resp)
        return resp
      }
    }
  }

  @action submitTerms = (terms?: boolean) => (this.termsAndCondition = terms)

  mangoPayOnCreateCardRegistration = async () => {
    const orderResponse = await this.handleSubmitWithRecaptcha()

    if (!orderResponse.success) {
      //handled on api method level
      throw new Error('ablefy.error')
    }

    return this.root.ordersStore
      .fetchItem(orderResponse.token, { username: this.root.sellerStore.item.username })
      .then((resp) => {
        const cardRegistration = resp.data.creditCard.mangoPay

        cardRegistration.id = cardRegistration.cardRegistrationId
        cardRegistration.currency = cardRegistration.currency.toUpperCase()
        cardRegistration.cardRegistrationURL = cardRegistration.cardRegistrationUrl

        return Object.keys(cardRegistration).reduce(
          (acc, key) => ({
            ...acc,
            [capitalizeFirstLetter(key)]: cardRegistration[key],
          }),
          {}
        ) as CreateCardRegistrationResult
      })
  }

  mangoPayOnCreatePayment = async (event: { CardId: string }) => {
    const resp = await this.root.paymentStore.confirmMangoCard(this.mangoPayOrderResponse?.token, event.CardId)

    if (resp.success) {
      setTimeout(() => {
        window.location.href = resp.data.redirectUrl
      }, 100)
    }
    this.setMangoPayOrderResponse(null)

    return {}
  }

  @action setMangoPayForm = (cardForm: CardForm) => {
    this.mangoPayForm = cardForm
  }

  submitMangoPayForm = () => {
    if (this.mangoPayForm) {
      try {
        this.mangoPayForm.disable()
        this.mangoPayForm.completePayment()
      } finally {
        const form = document.querySelector('.new-paymethod-content') as HTMLElement | null

        if (form !== null) {
          form.scrollIntoView()
        }
        this.mangoPayForm.enable()
      }
    }
  }

  constructor(root: ShopRootStore) {
    super()
    this.root = root
    this.childApi = createDealsApi(root.apiClientV2)
    makeObservable(this)
  }
}
